import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Easing, Image, ViewStyle } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import theme from '@themes/theme';

export interface WelcomeModalScreenProps {
  name: string;
  onGetStarted?: () => void;
  style?: ViewStyle;
}

const ILLUSTRATION = require('../../../assets/images/il_hot_air_ballon.png');
const GRID_SIZE = 16;
const GRID_COLUMNS = Array.from({ length: 32 }, (_, index) => index);
const GRID_ROWS = Array.from({ length: 14 }, (_, index) => index);

// ─── Bounce spring config ─────────────────────────────────────────────────────
// Simulates a spring: enters from below with overshoot, settles naturally.

function useBounceIn(delay = 0) {
  const translateY = useRef(new Animated.Value(32)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.92)).current;

  useEffect(() => {
    Animated.sequence([
      Animated.delay(delay),
      Animated.parallel([
        Animated.spring(translateY, {
          toValue: 0,
          damping: 14,
          stiffness: 180,
          mass: 0.9,
          useNativeDriver: true,
        }),
        Animated.spring(scale, {
          toValue: 1,
          damping: 14,
          stiffness: 180,
          mass: 0.9,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 220,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]),
    ]).start();
  }, [delay, opacity, scale, translateY]);

  return { translateY, opacity, scale };
}

export const WelcomeModalScreen = ({
  name,
  onGetStarted,
  style,
}: WelcomeModalScreenProps) => {
  const { t } = useTranslation('models');
  // Staggered bounce: illustration first, then text section
  const illus = useBounceIn(60);
  const content = useBounceIn(180);

  const description = t('projects.welcomeModal.jobsDescription');
  const buttonLabel = t('projects.welcomeModal.setupFirstJob');
  return (
    <Box
      backgroundColor='white'
      alignItems='center'
      borderRadius='l'
      borderWidth={1}
      width={500}
      style={[
        {
          maxWidth: '100%',
          padding: 40,
          overflow: 'hidden',
          position: 'relative',
          borderColor: 'rgba(232, 232, 232, 0.72)',
          boxShadow: '0 16px 36px rgba(10, 22, 41, 0.18)',
        },
        style,
      ]}>
      {/* ── Background grid ── */}
      <Box
        pointerEvents='none'
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
          height: 232,
          overflow: 'hidden',
          backgroundImage:
            'linear-gradient(to bottom, rgba(234,251,246,0.92) 0%, rgba(234,251,246,0.88) 62%, rgba(255,255,255,0.98) 100%)',
        }}>
        {GRID_COLUMNS.map((line) => (
          <Box
            key={`v-${line}`}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: line * GRID_SIZE,
              width: 1,
              backgroundColor: 'rgba(24, 168, 125, 0.2)',
            }}
          />
        ))}
        {GRID_ROWS.map((line) => (
          <Box
            key={`h-${line}`}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: line * GRID_SIZE,
              height: 1,
              backgroundColor: 'rgba(24, 168, 125, 0.2)',
            }}
          />
        ))}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundImage:
              'linear-gradient(to bottom, rgba(234,251,246,0) 0%, rgba(234,251,246,0.08) 58%, rgba(255,255,255,0.96) 100%)',
          }}
        />
      </Box>

      {/* ── Illustration — bounces in first ── */}
      <Animated.View
        style={{
          width: '100%',
          alignItems: 'center',
          height: 200,
          position: 'relative',
          zIndex: 1,
          opacity: illus.opacity,
          transform: [{ translateY: illus.translateY }, { scale: illus.scale }],
        }}>
        <Image
          source={ILLUSTRATION}
          resizeMode='contain'
          style={{ width: 210, height: 200 }}
        />
      </Animated.View>

      {/* ── Text + button — bounces in second ── */}
      <Animated.View
        style={{
          width: '100%',
          alignItems: 'center',
          paddingTop: theme.spacing.m,
          zIndex: 1,
          opacity: content.opacity,
          transform: [
            { translateY: content.translateY },
            { scale: content.scale },
          ],
        }}>
        <Text mb='xs' color='greenSecondary' variant='heading1'>
          {t('projects.welcomeModal.youreIn', { name })}
        </Text>

        <Text color='textPrimary' variant='heading2' mb='l'>
          {description}
        </Text>
        <Button
          height={56}
          variant='primary'
          fullWidth
          isSmall
          onPress={onGetStarted}
          accessibilityLabel={buttonLabel}>
          <Text color='white' variant='typography'>
            {buttonLabel}
          </Text>
        </Button>
      </Animated.View>
    </Box>
  );
};
